<template>
  <div>
    <page-list
      :list="list"
      :columns="columns"
      :group-actions="groupActions"
      :single-actions="singleActions"
      :showSearchbox="showSearchbox"
      :showGroupActions="showGroupActions"
      :export-data-options="exportDataOptions" />
  </div>
</template>

<script>
import { mapState } from 'vuex'
import * as R from 'ramda'
import WindowsMixin from '@/mixins/windows'
import ListMixin from '@/mixins/list'
import { numerify } from '@/filters'
import { currencyUnitMap } from '@/constants/currency'

export default {
  name: 'AccountPeriodList',
  components: {
  },
  mixins: [WindowsMixin, ListMixin],
  props: {
    resId: {
      type: String,
      require: true,
    },
    accountData: Object,
    id: String,
    getParams: {
      type: Object,
      default: () => ({}),
    },
  },
  data () {
    return {
      list: this.$list.createList(this, {
        id: this.id,
        apiVersion: 'v1',
        resource: 'blocking_accounts',
        getParams: this.getParam,
        filterOptions: {
          name: {
            label: this.$t('table.title.name'),
            filter: true,
            formatter: (val) => {
              return `month.equals(${(val + '').replace('-', '')})`
            },
          },
          enabled: {
            label: this.$t('bill.block_account_status'),
            dropdown: true,
            items: [
              { label: this.$t('bill.blocked_account'), key: true },
              { label: this.$t('bill.unblocked_account'), key: false },
            ],
          },
        },
      }),
      groupActions: [
        {
          label: this.$t('bill.set_block_account'),
          permission: 'block_accounts_update',
          action: () => {
            this.createDialog('SetBlockAccountDialog', {
              account_id: this.resId,
              data: this.list.selectedItems,
              columns: this.columns,
              onManager: this.onManager,
            })
          },
          meta: () => {
            return {
              validate: !!this.list.selectedItems.length,
            }
          },
        },
        {
          label: this.$t('common.more'),
          actions: () => {
            return [
              {
                label: this.$t('bill.supplement_bill'),
                permission: 'block_accounts_create',
                action: () => {
                  this.createDialog('BlockAccountCreateDialog', {
                    account_id: this.resId,
                    onManager: this.onManager,
                  })
                },
              },
            ]
          },
        },
      ],
      columns: [
        {
          title: this.$t('table.title.name'),
          field: 'month',
          formatter: ({ row }) => {
            const str = row.month + ''
            return `${str.slice(0, 4)}-${str.slice(4, 6)}`
          },
        },
        {
          field: 'status',
          title: this.$t('bill.block_account_status'),
          slots: {
            default: ({ row }) => {
              if (row.enabled) {
                return [<status specifyStatus={{ class: 'status-success', text: this.$t('bill.blocked_account') }} />]
              }
              return [<status specifyStatus={{ class: 'status-info', text: this.$t('bill.unblocked_account') }}/>]
            },
          },
          formatter: ({ row }) => {
            if (row.enabled) {
              return this.$t('bill.blocked_account')
            }
            return this.$t('bill.unblocked_account')
          },
        },
        {
          field: 'amount',
          title: this.$t('bill.text_308'),
          formatter: ({ row }) => {
            let sign = ''
            if (currencyUnitMap[row.currency]) {
              sign = currencyUnitMap[row.currency].sign || ''
            }
            return `${sign} ${numerify(row.amount, '0,0.00')}`
          },
          slots: {
            header: ({ column }) => {
              return [
                <span>
                  <span>{column.title}</span>
                  <a-tooltip class="ml-1" title={this.$t('bill.billing_amount_tip')}><a-icon type="question-circle" style="color: #aaa;" /></a-tooltip>
                </span>,
              ]
            },
            default: ({ row }) => {
              let sign = ''
              if (currencyUnitMap[row.currency]) {
                sign = currencyUnitMap[row.currency].sign || ''
              }
              const value = `${sign} ${numerify(row.amount, '0,0.00')}`
              const isSameMonth = row.month + '' === this.$moment().format('YYYYMM')
              const isHasDiff = row.amount && row.api_amount && Math.abs(row.amount - row.api_amount) > 0.01
              return [<span style={!isSameMonth && isHasDiff ? 'color: red;' : ''}>{value}</span>]
            },
          },
          needExportFormat: true,
        },
        {
          field: 'api_amount',
          title: this.$t('bill.api_amount'),
          formatter: ({ row }) => {
            let sign = ''
            if (currencyUnitMap[row.currency]) {
              sign = currencyUnitMap[row.currency].sign || ''
            }
            return `${sign} ${numerify(row.api_amount, '0,0.00')}`
          },
          slots: {
            header: ({ column }) => {
              return [
                <span>
                  <span>{column.title}</span>
                  <a-tooltip class="ml-1" title={this.$t('bill.api_amount_tip')}><a-icon type="question-circle" style="color: #aaa;" /></a-tooltip>
                </span>,
              ]
            },
          },
          needExportFormat: true,
        },
      ],
      singleActions: [
        {
          label: this.$t('bill.set_block_account'),
          permission: 'block_accountss_create',
          action: (obj) => {
            this.createDialog('SetBlockAccountDialog', {
              account_id: this.resId,
              data: [obj],
              columns: this.columns,
              onManager: this.onManager,
            })
          },
        },
      ],
    }
  },
  computed: {
    ...mapState('common', {
      billConfig: state => state.bill.globalConfig,
    }),
    exportDataOptions () {
      const items = this.columns.map(item => {
        return {
          key: item.field,
          label: item.title,
          formatter: item.formatter,
          needExportFormat: item.needExportFormat,
        }
      })
      return {
        title: this.$t('bill.accounting_period'),
        downloadType: 'local',
        items,
      }
    },
  },
  created () {
    this.fetchData()
  },
  methods: {
    fetchData () {
      this.list.fetchData()
    },
    getParam () {
      const getParams = (R.is(Function, this.getParams) ? this.getParams() : this.getParams)
      const ret = {
        details: false,
        account_id: this.resId,
        order_by: 'month',
        order: 'desc',
        ...getParams,
      }
      return ret
    },
    handleOpenSidepage (row) {
      this.sidePageTriggerHandle(this, 'ProjectsharingSidePage', {
        id: row.id,
        resource: 'project_sharings',
        apiVersion: 'v1',
        getParams: this.getParams,
      }, {
        list: this.list,
        hiddenActions: this.hiddenActions,
      })
    },
  },
}
</script>
